// ./src/components/features/Tasks/TaskBulkEditDrawer/TaskBulkEditDrawer.tsx
//
// Bulk-edit the selected tasks / appointments: optionally set a responsible party,
// a due date/time, and/or a category — each toggled on independently, so you only
// change what you check. The same value is applied to every selected task.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Stack, Switch, Box, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { TaskBulkInput } from "@/utils/api";
import { formatErrors, getFormErrors } from "@/utils";
import { useOptionalTasksContext } from "@/contexts/TasksContext";
import { AppDrawer, CategorySelect, DateTimeField } from "@/components/generic";
import { useFormSnapshot, useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";

import { ResponsiblePartySelect } from "../ResponsiblePartySelect/ResponsiblePartySelect";

dayjs.extend(utc);
dayjs.extend(timezone);

const resolveTz = (tz?: string | null): string => {
  if (tz) return tz;
  try {
    return dayjs.tz.guess();
  } catch {
    return "UTC";
  }
};

const zonedToUtc = (localStr?: string | null, tz?: string | null): string | null => {
  if (!localStr) return null;
  const d = dayjs.tz(localStr, resolveTz(tz));
  return d.isValid() ? d.utc().toISOString() : null;
};

export interface TaskBulkEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  ids: string[];
  eventId: string;
  eventTimezone?: string | null;
  onDone?: (count: number) => void;
}

export const TaskBulkEditDrawer: React.FC<TaskBulkEditDrawerProps> = ({
  opened,
  onClose,
  ids,
  eventId,
  eventTimezone,
  onDone,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const ctx = useOptionalTasksContext();
  const bulkUpdate = ctx?.bulkUpdate;
  const isBulkUpdating = ctx?.isBulkUpdating ?? false;
  const lastError = ctx?.lastError ?? null;
  const clearError = ctx?.clearError;

  // Which fields to apply + their values.
  const [setResponsible, setSetResponsible] = useState(false);
  const [responsibleKind, setResponsibleKind] = useState<string | null>(null);
  const [responsibleId, setResponsibleId] = useState<string | null>(null);

  const [setDue, setSetDue] = useState(false);
  const [dueLocal, setDueLocal] = useState<string | null>(null);

  const [setCategory, setSetCategory] = useState(false);
  const [categoryId, setCategoryId] = useState<string | null>(null);

  // Esc / a click outside / Cancel ask before discarding a changed choice (#123) — the same rule as every form drawer.
  const { isDirty, seedFrom, clear: clearSnapshot } = useFormSnapshot({ setResponsible, responsibleKind, responsibleId, setDue, dueLocal, setCategory, categoryId });

  useEffect(() => {
    if (opened) {
      setSetResponsible(false);
      setResponsibleKind(null);
      setResponsibleId(null);
      setSetDue(false);
      setDueLocal(null);
      setSetCategory(false);
      setCategoryId(null);
      clearError?.();
      seedFrom({ setResponsible: false, responsibleKind: null, responsibleId: null, setDue: false, dueLocal: null, setCategory: false, categoryId: null });
    } else {
      clearSnapshot();
    }
  }, [opened, clearError, seedFrom, clearSnapshot]);

  const { genericErrors } = useMemo(() => getFormErrors(lastError, []), [lastError]);

  const count = ids.length;
  const tzLabel = resolveTz(eventTimezone);
  const anySelected = setResponsible || setDue || setCategory;

  const handleSubmit = useCallback(async () => {
    if (!bulkUpdate || count === 0 || !anySelected) {
      return;
    }

    const params: TaskBulkInput = {};
    if (setResponsible) {
      params.responsibleKind = responsibleKind;
      params.responsibleId = responsibleId;
    }
    if (setDue) {
      params.scheduledAt = zonedToUtc(dueLocal, eventTimezone);
    }
    if (setCategory) {
      params.categoryId = categoryId;
    }

    const result = await bulkUpdate(ids, params);

    if (result.didSucceed()) {
      onDone?.(count);
      onClose();
    }
  }, [
    bulkUpdate,
    count,
    anySelected,
    setResponsible,
    responsibleKind,
    responsibleId,
    setDue,
    dueLocal,
    eventTimezone,
    setCategory,
    categoryId,
    ids,
    onDone,
    onClose,
  ]);

  // ⌘S / ⌘Enter only. Esc is AppDrawer's own — it honours `closable` and asks before discarding (#121, #123).
  useModalShortcuts({ onConfirm: handleSubmit, canConfirm: !isBulkUpdating && anySelected, enabled: opened });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("events:tasks.bulk.editTitle", { defaultValue: "Edit selected tasks" })}
      position="right"
      size="md"
      // A short form: on a phone the sheet is as tall as its content, not 92% of the screen.
      mobileFitContent
      closable={!isBulkUpdating}
      loading={isBulkUpdating}
      errors={formatErrors(genericErrors)}
      actionType="confirm-cancel"
      onConfirm={handleSubmit}
      confirmLabel={t("common:buttons.apply", { defaultValue: "Apply" })}
      confirmIcon="Check"
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={!anySelected}
      isDirty={isDirty}
    >
      <Stack gap="lg">
        <Text size="md" c="dimmed">
          {t("events:tasks.bulk.affected", {
            defaultValue: "Applies to {{count}} selected tasks.",
            count,
          })}
        </Text>

        <Text size="md" c="dimmed">
          {t("events:tasks.bulk.editHint", {
            defaultValue: "Turn on only what you want to change — the rest is left as-is.",
          })}
        </Text>

        <Stack gap="xs">
          <Switch
            label={t("events:tasks.bulk.setResponsible", { defaultValue: "Set who is responsible" })}
            checked={setResponsible}
            onChange={(e) => setSetResponsible(e.currentTarget.checked)}
            disabled={isBulkUpdating}
          />
          {setResponsible ? (
            <Box>
              <ResponsiblePartySelect
                eventId={eventId}
                value={{ kind: responsibleKind, id: responsibleId }}
                onChange={(kind, id) => {
                  setResponsibleKind(kind);
                  setResponsibleId(id);
                }}
                label=""
              />
            </Box>
          ) : null}
        </Stack>

        <Stack gap="xs">
          <Switch
            label={t("events:tasks.bulk.setDue", { defaultValue: "Set date & time" })}
            checked={setDue}
            onChange={(e) => setSetDue(e.currentTarget.checked)}
            disabled={isBulkUpdating}
          />
          {setDue ? (
            <DateTimeField
              description={`Shown in the event's timezone (${tzLabel})`}
              value={dueLocal}
              onChange={(_name, v) => setDueLocal(v)}
              clearable
              size="md"
            />
          ) : null}
        </Stack>

        <Stack gap="xs">
          <Switch
            label={t("events:tasks.bulk.setCategory", { defaultValue: "Set category" })}
            checked={setCategory}
            onChange={(e) => setSetCategory(e.currentTarget.checked)}
            disabled={isBulkUpdating}
          />
          {setCategory ? (
            <CategorySelect
              group="task"
              value={categoryId}
              onChange={setCategoryId}
              clearable
              size="md"
            />
          ) : null}
        </Stack>
      </Stack>
    </AppDrawer>
  );
};
